import { useAtom } from 'jotai';
import { useState } from 'react';
import { Alert, Badge, Box, Button, Flex, Link, Text, Tooltip } from '@optiaxiom/react';
import {
  IconAgent,
  IconArrowsRotate,
  IconCheckCircleSolid,
  IconCodeBranch,
  IconLock,
  IconSparkles,
  IconTag,
  IconToolbox,
  IconXmark,
} from '@optimizely/axiom-icons';

import { opalUserAtom } from '@frontend/atoms';
import { ConfirmationModal } from '@frontend/components/confirmationModal/components/ConfirmationModal';
import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { TruncatedList } from '@frontend/components/ui';
import { useInstanceId } from '@frontend/hooks';

import {
  useAgentDirectoryDetails,
  useInstallAgentFromDirectory,
} from '../../hooks/useAgentDirectory';
import { DirectoryAgent } from '../../services/agent-directory';

import { styles } from './AgentDetailsSidebar.css';
import { AgentDetailsSidebarSkeleton } from './AgentDetailsSidebarSkeleton';

type AgentDetailsSidebarProps = {
  agent: DirectoryAgent | undefined;
  open: boolean;
  onClose: () => void;
};

const iconSize = '20';

const AgentTypeIcon = {
  specialized: <IconAgent size={iconSize} />,
  workflow: <IconCodeBranch size={iconSize} />,
};

export function AgentDetailsSidebar({ agent, open, onClose }: AgentDetailsSidebarProps) {
  const [opalUser] = useAtom(opalUserAtom);
  const { instanceId } = useInstanceId();
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);

  const { data: agentDetails, isPending } = useAgentDirectoryDetails(
    agent?.agent_id,
    agent?.directory_version
  );

  const { mutate: installAgent, isPending: isInstalling } = useInstallAgentFromDirectory();

  const agentIcon = agent
    ? AgentTypeIcon[agent.agent_type] || <IconSparkles size={iconSize} />
    : null;

  const handleInstall = (forceUpdate = false) => {
    if (!agent || !instanceId || !opalUser?.id) return;

    installAgent({
      agentId: agent.agent_id,
      agentName: agentDetails?.name,
      request: {
        opal_instance_id: instanceId,
        user_id: opalUser.id,
        directory_version: agent.directory_version,
        force_update: forceUpdate,
      },
    });
  };

  const handleUpdateClick = () => {
    if (agent?.is_installed) {
      setShowUpdateConfirm(true);
    } else {
      handleInstall();
    }
  };

  const handleConfirmUpdate = () => {
    setShowUpdateConfirm(false);
    handleInstall(true);
  };

  return (
    <Sidebar open={open} dock="right" initialWidth={420} minWidth={300} maxWidth={600}>
      {isPending ? (
        <AgentDetailsSidebarSkeleton />
      ) : (
        <Flex flexDirection="column" h="full">
          {/* Main Content */}
          <Flex flexDirection="column" gap="16" p="24" flex="1" overflow="auto">
            {/* Header */}
            <Flex flexDirection="row" justifyContent="space-between" alignItems="start">
              <Flex flexDirection="row" alignItems="start" gap="12">
                <Flex className={styles.iconWrapper}>
                  <Flex
                    p="12"
                    rounded="md"
                    bg="bg.avatar.purple"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {agentIcon}
                  </Flex>
                  {agent?.is_installed &&
                    (agent.installed_version !== null &&
                    agent.installed_version < agent.directory_version ? (
                      <Tooltip content="New Version Available">
                        <IconArrowsRotate className={styles.updateAvailable} />
                      </Tooltip>
                    ) : (
                      <Tooltip content="Activated">
                        <IconCheckCircleSolid className={styles.installed} />
                      </Tooltip>
                    ))}
                </Flex>
                <Flex flexDirection="column" gap="0">
                  <Flex flexDirection="row" gap="6" alignItems="start">
                    <Text
                      fontSize="lg"
                      fontWeight="600"
                      color="fg.default"
                      className={styles.title}
                    >
                      {agentDetails?.name || 'Agent Details'}
                    </Text>
                    {agent?.is_system_agent && (
                      <Tooltip content="Optimizely Managed Agent">
                        <Box pt="4">
                          <IconLock aria-label="Optimizely Managed Agent lock icon" size="20" />
                        </Box>
                      </Tooltip>
                    )}
                  </Flex>
                  <Text fontSize="sm" color="fg.secondary" className={styles.title}>
                    @{agent?.agent_id || 'Unknown Agent ID'}
                  </Text>
                </Flex>
              </Flex>
              <Button
                icon={<IconXmark />}
                size="sm"
                appearance="subtle"
                onClick={onClose}
                aria-label="Close sidebar"
              />
            </Flex>

            {/* Content */}
            {agent && agentDetails ? (
              <Flex flexDirection="column" gap="16">
                {/* System Agent Info Banner */}
                {agent.is_system_agent && (
                  <Alert intent="information">
                    This agent is managed and automatically updated by Optimizely. To fully
                    customize it or integrate it into a workflow, simply create a copy.
                  </Alert>
                )}

                {/* Description */}
                <Flex flexDirection="column" gap="8">
                  <Flex className={styles.badgeWrapper}>
                    {agent.is_installed &&
                      (agent.installed_version !== null &&
                      agent.installed_version < agent.directory_version ? (
                        <Badge intent="information">New Version Available</Badge>
                      ) : (
                        <Badge intent="success">Activated</Badge>
                      ))}
                  </Flex>
                  <Text fontSize="md" color="fg.default">
                    {agentDetails.description || 'No description available'}
                  </Text>
                </Flex>

                {agentDetails?.details_url && (
                  <Flex flexDirection="row" alignItems="center" gap="12" py="8">
                    <Link href={agentDetails.details_url} external fontSize="sm">
                      View Documentation
                    </Link>
                  </Flex>
                )}

                {/* Tags and Enabled Tools */}
                <Flex flexDirection="column" gap="8" w="full">
                  {agentDetails.tags && agentDetails.tags.length > 0 && (
                    <Flex flexDirection="row" gap="8" alignItems="center" w="full">
                      <Tooltip content="Tags">
                        <IconTag size="16" color="fg.secondary" />
                      </Tooltip>
                      <TruncatedList lineClamp={1}>
                        {agentDetails.tags.map(tag => (
                          <Badge key={tag.name} intent="neutral">
                            {tag.name}
                          </Badge>
                        ))}
                      </TruncatedList>
                    </Flex>
                  )}
                  {agentDetails?.enabled_tools && agentDetails.enabled_tools.length > 0 && (
                    <Flex flexDirection="row" gap="8" alignItems="center" w="full">
                      <Tooltip content="Enabled Tools">
                        <IconToolbox size="16" color="fg.secondary" />
                      </Tooltip>
                      <TruncatedList lineClamp={1}>
                        {agentDetails.enabled_tools.map(tool => (
                          <Badge key={tool} intent="neutral">
                            {tool}
                          </Badge>
                        ))}
                      </TruncatedList>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            ) : (
              <Text color="fg.secondary">Failed to load agent details</Text>
            )}
          </Flex>

          {/* Footer */}
          {agent && agentDetails && (
            <Flex
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
              gap="8"
              p="24"
              pt="16"
            >
              <Button
                appearance="primary"
                onClick={handleUpdateClick}
                disabled={isInstalling || !instanceId || !opalUser?.id}
              >
                {isInstalling
                  ? 'Installing...'
                  : agent.is_installed
                    ? 'Update Agent'
                    : 'Activate Agent'}
              </Button>
              <Button appearance="default" onClick={onClose}>
                Close
              </Button>
            </Flex>
          )}
        </Flex>
      )}

      <ConfirmationModal
        title="Update Agent"
        isShown={showUpdateConfirm}
        onCancel={() => setShowUpdateConfirm(false)}
        onConfirm={handleConfirmUpdate}
        action="Update Agent"
        confirmLoading={isInstalling}
      >
        <Text>
          You&apos;re about to update this agent. All of your prior version history & agent details
          will be removed and cannot be recovered.
          <br />
          <br />
          Would you like to continue?
        </Text>
      </ConfirmationModal>
    </Sidebar>
  );
}
